import {
  historyConnectionLabel,
  historyDetailsLabel,
  historyEventLabel,
  historyMessageLabel,
  historyReceivedDate,
  historyRecordTypeLabel,
  historySignalLabel,
} from "./deviceHistoryPresentation";

const COLUMNS = [
  { key: "receivedAt", label: "Recebido em", width: 22 },
  { key: "recordType", label: "Tipo", width: 13 },
  { key: "connection", label: "Conexão", width: 18 },
  { key: "signal", label: "Sinal", width: 18 },
  { key: "event", label: "Evento", width: 28 },
  { key: "message", label: "Mensagem", width: 38 },
  { key: "details", label: "Detalhes", width: 70 },
];

function tableRows(history) {
  return history.map((item) => ({
    receivedAt: historyReceivedDate(item),
    recordType: historyRecordTypeLabel(item),
    connection: historyConnectionLabel(item),
    signal: historySignalLabel(item),
    event: historyEventLabel(item),
    message: historyMessageLabel(item),
    details: historyDetailsLabel(item),
  }));
}

function printableDate(value) {
  if (!(value instanceof Date)) return "—";
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(value);
}

function safeDeviceId(deviceId) {
  return String(deviceId || "device")
    .trim()
    .replace(/[^a-zA-Z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "") || "device";
}

function filename(deviceId, extension) {
  const date = new Date().toISOString().slice(0, 10);
  return `historico-${safeDeviceId(deviceId)}-${date}.${extension}`;
}

function downloadBlob(blob, targetFilename) {
  if (navigator.msSaveOrOpenBlob) {
    navigator.msSaveOrOpenBlob(blob, targetFilename);
    return;
  }
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = targetFilename;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  window.setTimeout(() => {
    link.remove();
    URL.revokeObjectURL(url);
  }, 1000);
}

export async function exportDeviceHistoryExcel(deviceId, history) {
  const excelModule = await import("exceljs");
  const ExcelJS = excelModule.default || excelModule;
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Datacasting";
  workbook.created = new Date();

  const worksheet = workbook.addWorksheet("Histórico", {
    views: [{ state: "frozen", ySplit: 1 }],
    properties: { defaultRowHeight: 20 },
  });
  worksheet.columns = COLUMNS.map((column) => ({
    header: column.label,
    key: column.key,
    width: column.width,
  }));
  worksheet.addRows(tableRows(history));
  worksheet.autoFilter = `A1:G${Math.max(history.length + 1, 1)}`;
  worksheet.getColumn("receivedAt").numFmt = "dd/mm/yyyy hh:mm:ss";

  worksheet.getRow(1).height = 26;
  worksheet.getRow(1).eachCell((cell) => {
    cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF17324D" } };
    cell.alignment = { vertical: "middle", horizontal: "left" };
  });
  worksheet.eachRow((row, rowNumber) => {
    if (rowNumber === 1) return;
    row.eachCell((cell) => {
      cell.alignment = { vertical: "middle", horizontal: "left", wrapText: true };
    });
    const connectionCell = row.getCell(3);
    if (connectionCell.value === "Conectado" || connectionCell.value === "Desconectado") {
      connectionCell.font = {
        color: { argb: connectionCell.value === "Conectado" ? "FF198553" : "FFB13E3E" },
      };
    }
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob(
    [new Uint8Array(buffer)],
    { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" },
  );
  downloadBlob(blob, filename(deviceId, "xlsx"));
}

export async function exportDeviceHistoryPdf(deviceId, history) {
  const [{ jsPDF }, tableModule] = await Promise.all([
    import("jspdf"),
    import("jspdf-autotable"),
  ]);
  const autoTable = tableModule.default || tableModule.autoTable;
  const documentPdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const rows = tableRows(history);

  documentPdf.setFont("helvetica", "bold");
  documentPdf.setFontSize(16);
  documentPdf.text(`Histórico do device ${deviceId}`, 14, 16);
  documentPdf.setFont("helvetica", "normal");
  documentPdf.setFontSize(9);
  documentPdf.text(`${history.length} registros exportados em ${printableDate(new Date())}`, 14, 22);

  autoTable(documentPdf, {
    startY: 28,
    head: [COLUMNS.map((column) => column.label)],
    body: rows.map((row) => [
      printableDate(row.receivedAt),
      row.recordType,
      row.connection,
      row.signal,
      row.event,
      row.message,
      row.details,
    ]),
    theme: "striped",
    margin: { left: 14, right: 14, bottom: 14 },
    styles: { font: "helvetica", fontSize: 7, cellPadding: 1.8, overflow: "linebreak" },
    headStyles: { fillColor: [23, 50, 77], textColor: 255, fontStyle: "bold" },
    columnStyles: {
      0: { cellWidth: 38 },
      1: { cellWidth: 20 },
      2: { cellWidth: 27 },
      3: { cellWidth: 27 },
      4: { cellWidth: 35 },
      5: { cellWidth: 45 },
      6: { cellWidth: 77 },
    },
  });

  const pageCount = documentPdf.getNumberOfPages();
  for (let page = 1; page <= pageCount; page += 1) {
    documentPdf.setPage(page);
    documentPdf.setFont("helvetica", "normal");
    documentPdf.setFontSize(8);
    documentPdf.setTextColor(100);
    documentPdf.text(`Página ${page} de ${pageCount}`, 283, 200, { align: "right" });
  }
  const blob = documentPdf.output("blob");
  downloadBlob(blob, filename(deviceId, "pdf"));
}
